import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ResponseCatalog, RequestParams } from 'Models'
import {
	BankGuarantee,
	BankGuaranteeBuild,
	BankGuaranteeDeliveryData,
	BankGuaranteeFull,
	BankGuaranteeSaveModel,
	BgDict,
	BgMetaModel,
	BgWarning,
	ChangeSocketBg,
	CreateBg,
	DependentFieldsBg,
	InitialParamsSocketBg,
	Calendar,
	BgMessage,
	CreateBuildBg,
	LeadBg,
	SendReleasedDocsParams,
	BgPublicationModel,
	CreateBgPublicationModel,
	BgHistoryType,
} from 'Pages/ibro'
import { Callback } from 'Src/types/utilTypes'
import { SocketMessageTypes } from 'Modules/Socket'

export type BgRequestState = {
	data: ResponseCatalog<BankGuarantee>
	meta?: BgMetaModel
	requestParams: RequestParams
	filtersIsVisible: boolean
	currentBg: BankGuaranteeFull | null
	dependentFields: DependentFieldsBg | null
	needSave: boolean
	dict: BgDict | null
	changesSocket: ChangeSocketBg | null
	initialParamsSocket: InitialParamsSocketBg | null
	awaitingCalculateCommission: boolean
	bgWarning: BgWarning
	isRequiredKpp: boolean
	agreementStatus: LeadBg | null
	build: BankGuaranteeBuild | null
	listOfCreated: BankGuaranteeFull[] | null
	awaitingProcess: boolean
	bgMessage: BgMessage | null
	calendarList: ResponseCatalog<Calendar>
	commissionState: boolean
	commissionReject: boolean
	isSpkLoading: boolean
	spkResponse: string[]
	history: BgHistoryType
}

export const bgRequestInitialState: BgRequestState = {
	data: new ResponseCatalog<BankGuarantee>(),
	meta: undefined,
	requestParams: new RequestParams(),
	filtersIsVisible: false,
	currentBg: null,
	dependentFields: null,
	needSave: false,
	dict: null,
	changesSocket: null,
	initialParamsSocket: null,
	awaitingCalculateCommission: false,
	bgWarning: new BgWarning(),
	isRequiredKpp: false,
	agreementStatus: null,
	build: null,
	listOfCreated: null,
	awaitingProcess: false,
	bgMessage: null,
	calendarList: new ResponseCatalog<Calendar>(),
	commissionState: false,
	commissionReject: false,
	isSpkLoading: false,
	spkResponse: [],
	history: null,
}

export namespace BgRequestNs {
	export type GetList = PayloadAction<{
		params?: RequestParams
		clear?: boolean
		callback?: Callback
	}>
	export type SetList = PayloadAction<ResponseCatalog<BankGuarantee>>
	export type SetMeta = PayloadAction<BgMetaModel>
	export type SetRequestParams = PayloadAction<RequestParams>

	export type SetDict = PayloadAction<BgDict>

	export type GetCalculateCommission = PayloadAction<ChangeSocketBg>
	export type SetAwaitingCalculateCommission = PayloadAction<boolean>

	export type SetFiltersIsVisible = PayloadAction<boolean>

	export type SetDependentFields = PayloadAction<DependentFieldsBg>

	export type GetItem = PayloadAction<number>
	export type GetItemWithCheckStatus = PayloadAction<number>
	export type SetItem = PayloadAction<{
		data: BankGuaranteeFull
		resetNeedSave?: boolean
	}>

	export type SendRequest = PayloadAction<{
		data: BankGuaranteeSaveModel
		params?: Pick<BankGuaranteeFull, 'status' | 'external_id' | 'guaranteetemplate'>
	}>
	export type SaveRequest = PayloadAction<{
		data: BankGuaranteeSaveModel
		refreshOnlySingle?: boolean
		showNotification?: boolean
		hideLoader?: boolean
	}>

	export type SetNeedSave = PayloadAction<boolean>
	export type UpdateBgItemList = PayloadAction<Partial<BankGuarantee> & { id: number }>

	export type GetBankData = PayloadAction<{ id: number; data: ChangeSocketBg }>

	export type SendBuildBg = PayloadAction<{
		data: CreateBuildBg
	}>
	export type SendCreateBg = PayloadAction<CreateBg>

	export type SetBgWarning = PayloadAction<BgWarning>
	export type SetKppRequired = PayloadAction<boolean>
	export type SetAgreementStatus = PayloadAction<LeadBg | null>
	export type SetBuildData = PayloadAction<BankGuaranteeBuild | null>
	export type SetListOfCreated = PayloadAction<BankGuaranteeFull[]>
	export type SetAwaitingProcess = PayloadAction<boolean>
	export type UpdateStatus = PayloadAction<{
		id: number
		status: boolean
		request_type: SocketMessageTypes
		errors?: unknown[]
		info?: unknown
		changes?: ChangeSocketBg
	}>
	export type ChangeSocket = PayloadAction<{
		change: ChangeSocketBg
		params?: InitialParamsSocketBg
	}>
	export type SetCalculateCommission = PayloadAction<{
		status: boolean
		change: ChangeSocketBg
		params?: InitialParamsSocketBg
		errors?: unknown[]
	}>
	export type ChangeSendAddress = PayloadAction<
		Pick<BankGuaranteeFull, 'id'> & BankGuaranteeDeliveryData
	>
	export type ConfirmedChangeSendAddress = PayloadAction<{ id: number; status: boolean }>
	export type CloneRequestComplete = PayloadAction<{ id: number; clone_request_id: number }>
	export type SetBgMessage = PayloadAction<BgMessage>

	export type GetCalendarList = PayloadAction<{
		params: RequestParams
		clear?: boolean
		callback?: Callback
	}>
	export type SetCalendarList = PayloadAction<ResponseCatalog<Calendar>>
	export type UpdateCurrentElements = PayloadAction<{ id: number }>
	export type SendToRework = PayloadAction<{ id: number }>
	export type AssignRequest = PayloadAction<{
		id: number
		partner_id: number
		created_by_user_id: number
		clear_data: boolean
	}>
	export type AddFavorite = PayloadAction<{ id: number }>
	export type DeleteFavorite = PayloadAction<{ id: number }>
	export type SetCommissionState = PayloadAction<boolean>
	export type CheckLimit = PayloadAction<{ data: BankGuaranteeSaveModel }>
	export type SetCommissionReject = PayloadAction<boolean>
	export type UpdateFlag = PayloadAction<Pick<BankGuaranteeFull, 'flagpowerofattorney' | 'id'>>
	export type SetSpkLoading = PayloadAction<boolean>
	export type SetSpkResponse = PayloadAction<string[]>
	export type GetSpk = PayloadAction<string>
	export type SendReleasedDocs = PayloadAction<{ id: number; params: SendReleasedDocsParams }>
	export type CreatePublication = PayloadAction<{
		data: CreateBgPublicationModel
		callback: Callback
	}>
	export type UpdatePublication = PayloadAction<{
		id: number
		data: CreateBgPublicationModel
		callback: Callback
	}>
	export type DeletePublication = PayloadAction<{ id: number; callback: Callback }>
	export type SetPublication = PayloadAction<BgPublicationModel>
	export type GetHistory = PayloadAction<number>
	export type SetHistory = PayloadAction<BgHistoryType>
}

export const bgRequestSlice = createSlice({
	name: 'bgRequest',
	initialState: bgRequestInitialState,
	reducers: {
		getList: (state, action: BgRequestNs.GetList) => {},
		setList: (state, action: BgRequestNs.SetList) => {
			state.data = action.payload
		},
		setMeta: (state, action: BgRequestNs.SetMeta) => {
			state.meta = action.payload
		},
		setRequestParams: (state, action: BgRequestNs.SetRequestParams) => {
			state.requestParams = action.payload
		},
		clearAll: (state) => {
			state.data = new ResponseCatalog<BankGuarantee>()
			state.requestParams = new RequestParams()
			state.meta = undefined
			state.dependentFields = null
			state.changesSocket = null
			state.filtersIsVisible = false
			state.awaitingProcess = false
		},

		getDict: () => {},
		setDict: (state, action: BgRequestNs.SetDict) => {
			state.dict = action.payload
		},

		getCalculateCommission: (state, action: BgRequestNs.GetCalculateCommission) => {},
		setAwaitingCalculateCommission: (state, action: BgRequestNs.SetAwaitingCalculateCommission) => {
			state.awaitingCalculateCommission = action.payload
		},
		setFiltersIsVisible: (state, action: BgRequestNs.SetFiltersIsVisible) => {
			state.filtersIsVisible = action.payload
		},
		getItem: (state, action: BgRequestNs.GetItem) => {},
		getItemWithCheckStatus: (state, action: BgRequestNs.GetItemWithCheckStatus) => {},
		setItem: (state, action: BgRequestNs.SetItem) => {
			state.currentBg = action.payload.data
			state.needSave = action.payload.resetNeedSave ? false : state.needSave
		},

		saveRequest: (state, action: BgRequestNs.SaveRequest) => {},
		sendRequest: (state, action: BgRequestNs.SendRequest) => {},
		setNeedSave: (state, action: BgRequestNs.SetNeedSave) => {
			state.needSave = action.payload
		},
		updateBgItemList: (state, action: BgRequestNs.UpdateBgItemList) => {
			state.data.entries = state.data.entries?.map((item) =>
				item.id !== action.payload.id ? item : Object.assign(item, action.payload)
			)
		},
		getBankData: (state, action: BgRequestNs.GetBankData) => {},

		getDependentFields: (state) => {},
		setDependentFields: (state, action: BgRequestNs.SetDependentFields) => {
			state.dependentFields = action.payload
		},
		clearItem: (state) => {
			state.currentBg = null
			state.needSave = false
			state.changesSocket = null
			state.initialParamsSocket = null
			state.awaitingCalculateCommission = false
			state.bgMessage = null
		},

		downLoadGuaranteeTemplate: () => {},

		buildBg: (state, action: BgRequestNs.SendBuildBg) => {},
		createBg: (state, action: BgRequestNs.SendCreateBg) => {},

		clearCreateData: (state) => {
			state.bgWarning = new BgWarning()
			state.build = null
			state.listOfCreated = null
			state.isRequiredKpp = false
			state.awaitingProcess = false
		},

		setBgWarning: (state, action: BgRequestNs.SetBgWarning) => {
			state.bgWarning = action.payload
		},
		clearBgWarning: (state) => {
			state.bgWarning = new BgWarning()
		},
		setKppRequired: (state, action: BgRequestNs.SetKppRequired) => {
			state.isRequiredKpp = action.payload
		},
		setAgreementStatus: (state, action: BgRequestNs.SetAgreementStatus) => {
			state.agreementStatus = action.payload
		},
		setBuildData: (state, action: BgRequestNs.SetBuildData) => {
			state.build = action.payload
			state.bgWarning = new BgWarning()
		},
		clearBuild: (state) => {
			state.build = null
			state.bgWarning = new BgWarning()
		},
		setListOfCreated: (state, action: BgRequestNs.SetListOfCreated) => {
			state.listOfCreated = action.payload
		},
		setAwaitingProcess: (state, action: BgRequestNs.SetAwaitingProcess) => {
			state.awaitingProcess = action.payload
		},

		changeSendAddress: (state, action: BgRequestNs.ChangeSendAddress) => {},

		updateStatusItem: (state, action: BgRequestNs.UpdateStatus) => {},
		setCalculateCommission: (state, action: BgRequestNs.SetCalculateCommission) => {},
		confirmedChangeSendAddress: (state, action: BgRequestNs.ConfirmedChangeSendAddress) => {},
		cloneRequestComplete: (state, action: BgRequestNs.CloneRequestComplete) => {},
		changeSocket: (state, action: BgRequestNs.ChangeSocket) => {
			state.changesSocket = action.payload.change
			state.initialParamsSocket = action.payload?.params || null
		},

		setBgMessage: (state, action: BgRequestNs.SetBgMessage) => {
			state.bgMessage = action.payload
		},
		clearBgMessage: (state) => {
			state.bgMessage = null
		},

		getCalendarList: (state, action: BgRequestNs.GetCalendarList) => {},
		setCalendarList: (state, action: BgRequestNs.SetCalendarList) => {
			state.calendarList = action.payload
		},

		updateCurrentElements: (state, action: BgRequestNs.UpdateCurrentElements) => {},
		updateItem: (state, action: BgRequestNs.UpdateCurrentElements) => {},
		sendToRework: (state, action: BgRequestNs.SendToRework) => {},

		setCommissionState: (state, action: BgRequestNs.SetCommissionState) => {
			state.commissionState = action.payload
		},
		assignRequest: (state, action: BgRequestNs.AssignRequest) => {},
		addFavorite: (state, action: BgRequestNs.AddFavorite) => {},
		deleteFavorite: (state, action: BgRequestNs.DeleteFavorite) => {},
		checkLimit: (state, action: BgRequestNs.CheckLimit) => {},
		setCommissionReject: (state, action: BgRequestNs.SetCommissionReject) => {
			state.commissionReject = action.payload
		},
		updateFlag: (state, action: BgRequestNs.UpdateFlag) => {},
		getDefaultItem: (state, action: BgRequestNs.GetItem) => {},
		getDirectorItem: (state, action: BgRequestNs.GetItem) => {},
		setSpkLoading: (state, action: BgRequestNs.SetSpkLoading) => {
			state.isSpkLoading = action.payload
		},
		setSpkResponse: (state, action: BgRequestNs.SetSpkResponse) => {
			state.spkResponse = action.payload
		},
		getSpk: (state, action: BgRequestNs.GetSpk) => {},
		sendReleasedDocs: (state, action: BgRequestNs.SendReleasedDocs) => {},
		createPublication: (state, action: BgRequestNs.CreatePublication) => {},
		updatePublication: (state, action: BgRequestNs.UpdatePublication) => {},
		deletePublication: (state, action: BgRequestNs.DeletePublication) => {},
		setPublication: (state, action: BgRequestNs.SetPublication) => {
			state.currentBg.publication = action.payload
		},
		clearPublication: (state) => {
			state.currentBg.publication = null
		},
		getHistory: (state, action: BgRequestNs.GetHistory) => {},
		setHistory: (state, action: BgRequestNs.SetHistory) => {
			state.history = action.payload
		},
	},
})
